/**
 * ctaTracking.js — telemetría de los CTAs.
 *
 * Dos mecanismos distintos que conviene no confundir:
 *
 * 1. **Atribución de campañas.** Un link `mytapstar.com/go/<cta_id>/` (generado
 *    sin código desde el panel admin) hace un 302 que añade `?cta=<cta_id>` al
 *    destino y fija la cookie de primera parte `mts_cta`. `getCtaId()` lee ese
 *    id para que `Register.jsx` lo mande en el alta.
 *
 * 2. **CTAs internos de la app.** `trackCtaClick()` y `useCtaImpression()`
 *    miden los botones que pintamos nosotros (landing, precios, modales de
 *    upsell, bloqueos de Pro…), declarados en `ctaRegistry.js`.
 *
 * Nada de lo que hay aquí puede romper una navegación: si el tracking falla, el
 * botón tiene que seguir llevando a donde lleva. De ahí que todo vaya envuelto
 * en `try/catch` y que ningún error se propague.
 */
import { useCallback, useEffect, useRef } from 'react';

import { CTA_IDS } from '@/lib/ctaRegistry';

const CLICK_ENDPOINT = '/api/v1/cta/click/';
const VIEW_ENDPOINT = '/api/v1/cta/view/';

/**
 * El `cta_id` de campaña que trae la URL, o `null`.
 *
 * Solo mira `?cta=`. **No** hay respaldo por cookie, y no es un olvido: la
 * cookie `mts_cta` que pone `/go/<cta_id>/` es `HttpOnly`, así que
 * `document.cookie` no la ve por construcción. Durante un tiempo esto intentó
 * leerla y era un no-op silencioso — la cookie existía, duraba 90 días y no
 * servía para nada.
 *
 * Quien lee la cookie es el SERVIDOR, en `RegisterView`, que ya la tiene en
 * `request.COOKIES`. Así que un alta sin `?cta=` en la URL sigue atribuyéndose
 * bien; simplemente no pasa por aquí. No reintroduzcas el `document.cookie`.
 */
export function getCtaId() {
  try {
    return new URLSearchParams(window.location.search).get('cta') || null;
  } catch {
    return null;
  }
}

/**
 * Propaga el `?cta=` vigente a una URL interna.
 *
 * Para las navegaciones que se construyen a mano y se comerían el parámetro:
 * `Landing.jsx` y `Pricing.jsx` mandan a `/register?redirect=…` y, sin esto, el
 * `?cta=` con el que aterrizó el visitante se pierde por el camino.
 *
 * Con el respaldo por cookie del servidor ya no es imprescindible, pero sí
 * cubre el caso de un link pegado con `?cta=` a pelo, sin pasar por `/go/` y
 * por tanto sin cookie ninguna.
 *
 * `respaldo` es el id del BOTÓN que provoca la navegación, y solo se usa cuando
 * no hay campaña. Así un botón público de «Crear cuenta» se atribuye el registro
 * que trae —y, más tarde, el alta de plan que salga de él (decisiones §124)— sin
 * quitarle nada a la campaña que trajo al visitante, que sigue teniendo
 * preferencia. La precedencia es la misma que en el resto del sistema: lo que
 * viene de fuera gana al botón interno.
 *
 * Ojo: eso último lo garantiza el SERVIDOR desde §126, no esta función. Aquí el
 * respaldo solo entra si `?cta=` ya no está en la URL, y eso pasa en cuanto el
 * visitante cruza una página sin instrumentar (landing → /pricing → registro).
 * Mientras la regla vivió solo aquí, ese camino le regalaba el registro al botón.
 *
 * @param {string} url
 * @param {string} [respaldo] Id declarado en `lib/ctaRegistry.js`, con meta
 *   `signup`: es la única que `RegisterView` acepta de un id interno.
 */
export function withCta(url, respaldo) {
  try {
    const ctaId = getCtaId() || respaldo;
    if (!ctaId) return url;
    return `${url}${url.includes('?') ? '&' : '?'}cta=${encodeURIComponent(ctaId)}`;
  } catch {
    return url;
  }
}

// ── CTAs internos ───────────────────────────────────────────────────────────

/**
 * Manda un cuerpo JSON a un endpoint de ingesta sin bloquear la navegación.
 *
 * `sendBeacon` y NO el `httpClient` de axios. Es una excepción consciente a la
 * regla de usar siempre `httpClient`: un CTA navega, y al descargar la página
 * el navegador CANCELA cualquier XHR en vuelo — el evento se perdería justo en
 * los clicks que más importan. `sendBeacon` encola la petición en el proceso
 * del navegador y sobrevive al unload; es literalmente su único motivo de
 * existir. Mismo problema que ya resuelve `PreviewPanel.jsx` con `keepalive`
 * para los clicks del «Todo en 1» público.
 *
 * El `Blob` con `type: 'application/json'` es obligatorio: sin él `sendBeacon`
 * manda `text/plain` y el parser de DRF lo rechaza. Como es mismo origen (en
 * desarrollo vía el proxy de Vite y en producción de serie), ese Content-Type
 * no dispara preflight de CORS.
 */
function beacon(endpoint, body) {
  const payload = JSON.stringify(body);
  try {
    const blob = new Blob([payload], { type: 'application/json' });
    if (navigator.sendBeacon?.(endpoint, blob)) return;
  } catch {
    // Sigue al fetch de respaldo.
  }
  try {
    fetch(endpoint, {
      method: 'POST',
      body: payload,
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    }).catch(() => {});
  } catch {
    // El tracking NUNCA rompe una navegación.
  }
}

function isDeclared(ctaId) {
  if (CTA_IDS.has(ctaId)) return true;
  if (import.meta.env?.DEV) {
    // Un aviso y no una excepción: en producción un id retirado que sobreviva
    // en un bundle cacheado no debe romper la página de nadie.
    console.warn(`[cta] «${ctaId}» no está declarado en lib/ctaRegistry.js: no se manda nada.`);
  }
  return false;
}

/**
 * Registra un click en un CTA interno.
 *
 * `path` va sin `search` a propósito: la query puede llevar un `?redirect=` con
 * una ruta privada, o el token de recuperación de contraseña. (El backend lo
 * vuelve a recortar; esto es solo para no mandarlo de entrada.)
 */
export function trackCtaClick(ctaId) {
  try {
    if (!ctaId || !isDeclared(ctaId)) return;
    beacon(CLICK_ENDPOINT, {
      ctaId,
      path: window.location.pathname,
      lang: document.documentElement.lang || '',
    });
  } catch {
    // Ídem: nunca propagar.
  }
}

// ── Impresiones ─────────────────────────────────────────────────────────────

/**
 * Buffer de impresiones pendientes de mandar.
 *
 * No se manda una petición por CTA que aparece en pantalla: una sola pasada por
 * la landing pintaría media docena y serían media docena de peticiones. Se
 * acumulan y se sueltan de golpe al ocultarse la pestaña.
 */
let pendingViews = [];
let flushHooked = false;

/**
 * La deduplicación —una impresión por sesión, por CTA y por día— se hace AQUÍ y
 * no en el servidor porque aquí es gratis: `sessionStorage` ya está por sesión,
 * y así el CTR mide «a cuánta gente se le enseñó» en vez de «cuántas veces se
 * repintó», que es lo que subiría cada vez que alguien hace scroll arriba y
 * abajo o vuelve a la pestaña.
 *
 * El día entra en la clave para que una sesión abierta durante días no deje de
 * contar a partir del primero.
 */
function alreadySeenThisSession(ctaId) {
  try {
    const key = `cta:seen:${new Date().toISOString().slice(0, 10)}:${ctaId}`;
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
    return false;
  } catch {
    // Navegación privada o almacenamiento bloqueado: se prefiere contar de más
    // a no contar nada.
    return false;
  }
}

function flushViews() {
  if (!pendingViews.length) return;
  const views = pendingViews;
  pendingViews = [];
  beacon(VIEW_ENDPOINT, { views });
}

function hookFlush() {
  if (flushHooked || typeof document === 'undefined') return;
  flushHooked = true;
  // `visibilitychange` → hidden y `pagehide` son los dos únicos eventos
  // fiables al cerrar o cambiar de pestaña en móvil; `beforeunload` no dispara
  // en iOS. Se enganchan los dos y el buffer vacío hace idempotente el segundo.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushViews();
  });
  window.addEventListener('pagehide', flushViews);
}

/**
 * Marca un CTA como visto cuando entra de verdad en pantalla.
 *
 * Devuelve una `ref` para colgar del elemento. Umbral del 50 % y una sola vez:
 * un CTA que asoma un píxel al final del viewport no se ha «enseñado», y
 * contarlo hundiría el CTR de los que están abajo del todo.
 *
 * @param {string} ctaId Id declarado en `lib/ctaRegistry.js`.
 */
export function useCtaImpression(ctaId) {
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  const setRef = useCallback((node) => {
    elementRef.current = node;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node || !ctaId || !CTA_IDS.has(ctaId)) return;
    if (typeof IntersectionObserver === 'undefined') return;

    try {
      hookFlush();
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();
          observerRef.current = null;
          if (!alreadySeenThisSession(ctaId)) {
            pendingViews.push(ctaId);
          }
        }
      }, { threshold: 0.5 });
      observer.observe(node);
      observerRef.current = observer;
    } catch {
      // Sin observer no hay impresiones, pero el botón sigue funcionando.
    }
  }, [ctaId]);

  useEffect(() => () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  return setRef;
}

/** Para tests: vacía el buffer sin mandar nada. */
export function __resetCtaImpressions() {
  pendingViews = [];
}

/** Para tests: qué hay pendiente de mandar. */
export function __pendingCtaImpressions() {
  return [...pendingViews];
}
